/**
 * Form block — frontend view script.
 *
 * Initialises all form blocks on the page:
 *   - Repopulates field values after a failed non-async submission.
 *   - Displays server-side validation errors injected via a JSON data island.
 *   - Attaches an async fetch handler to forms with data-async="true".
 *
 * Uses a readyState guard instead of DOMContentLoaded so the code runs
 * regardless of whether the script is loaded with defer, in the footer,
 * or anywhere else on the page.
 */

function initFormBlocks() {
	document.querySelectorAll( 'form.wp-block-form-blocks-form' ).forEach( ( form ) => {
		const formId = form.dataset.formId;

		if ( formId ) {
			const data = readFormData( formId );

			// Restore submitted values first, then overlay the error state.
			if ( data && data.values ) {
				repopulateForm( form, data.values );
			}
			if ( data && data.errors ) {
				showFieldErrors( form, data.errors );
			}
		}

		if ( form.dataset.async === 'true' ) {
			attachAsyncHandler( form );
		}
	} );
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initFormBlocks );
} else {
	initFormBlocks();
}

/**
 * Read and parse a form's JSON data island, if the server rendered one.
 *
 * Values/errors from a failed non-async submission are embedded as an inert
 * `application/json` script tag rather than interpolated into an executable
 * `<script>` block — the browser never treats this content as code, so
 * there's no JS-source escaping surface to get wrong. The element is removed
 * once read so a stale copy doesn't linger in the DOM.
 *
 * @param {string} formId
 * @return {Object|null}
 */
function readFormData( formId ) {
	const el = document.querySelector(
		`script.form-blocks-data[data-form-id="${ CSS.escape( formId ) }"]`
	);
	if ( ! el ) return null;

	let data = null;
	try {
		data = JSON.parse( el.textContent );
	} catch {
		data = null;
	}
	el.remove();

	return data;
}

/**
 * Attach a submit interceptor to a single async form.
 *
 * @param {HTMLFormElement} form
 */
function attachAsyncHandler( form ) {
	const statusEl      = form.querySelector( '.form-blocks-status' );
	const networkErrMsg = form.dataset.msgNetworkError ||
		'An unexpected error occurred. Please try again.';
	const sendingMsg     = form.dataset.msgSending || 'Sending...';

	form.addEventListener( 'submit', async ( event ) => {
		event.preventDefault();

		const submitBtn     = form.querySelector( '[type="submit"]' );
		const originalLabel = submitBtn ? submitBtn.innerHTML : null;

		setSubmitting( form, submitBtn, true, sendingMsg );
		hideStatus( statusEl );
		clearFieldErrors( form );

		try {
			// getAttribute(), not form.action — the form has a hidden field
			// named "action" (required by WordPress's own admin-post.php
			// dispatch convention), and a named form control shadows the
			// .action IDL property. form.action would resolve to that
			// <input> element itself, not the URL, once such a field exists.
			const response = await fetch( form.getAttribute( 'action' ), {
				method: 'POST',
				// Do NOT set Content-Type — the browser derives it from FormData,
				// including the correct multipart boundary for file uploads.
				headers: { 'X-Form-Blocks-Async': '1' },
				body: new FormData( form ),
			} );

			const data = await response.json();

			if ( data.errors ) {
				showFieldErrors( form, data.errors );
			}

			showStatus( statusEl, data.success, data.message );

			if ( data.success ) {
				form.reset();
			}
		} catch {
			showStatus( statusEl, false, networkErrMsg );
		} finally {
			setSubmitting( form, submitBtn, false, originalLabel );
		}
	} );
}

/**
 * Repopulate form field values from the previously submitted data.
 * Called after a non-async submission that failed, so the user doesn't
 * have to retype everything.
 *
 * @param {HTMLFormElement} form
 * @param {Object}          values  { fieldName: submittedValue }
 */
function repopulateForm( form, values ) {
	Object.entries( values ).forEach( ( [ name, value ] ) => {
		form.querySelectorAll( `[name="${ CSS.escape( name ) }"]` ).forEach( ( input ) => {
			const type = ( input.type || '' ).toLowerCase();
			if ( type === 'checkbox' ) {
				input.checked = ( input.value === value || value === '1' || value === 'on' );
			} else if ( type === 'radio' ) {
				input.checked = ( input.value === value );
			} else {
				// Covers text, email, number, date, time, textarea, select.
				input.value = value;
			}
		} );
	} );
}

/**
 * Mark fields with validation errors and populate their error spans.
 *
 * @param {HTMLFormElement} form
 * @param {Object}          errors  { fieldName: errorMessage }
 */
function showFieldErrors( form, errors ) {
	Object.entries( errors ).forEach( ( [ fieldName, errorMessage ] ) => {
		const input = form.querySelector( `[name="${ CSS.escape( fieldName ) }"]` );
		if ( ! input ) return;

		input.setAttribute( 'aria-invalid', 'true' );

		const errorId = input.getAttribute( 'aria-describedby' );
		if ( errorId ) {
			const errorEl = document.getElementById( errorId );
			if ( errorEl ) {
				errorEl.textContent = errorMessage;
			}
		}
	} );
}

/**
 * Remove all field-level error indicators from a form.
 *
 * @param {HTMLFormElement} form
 */
function clearFieldErrors( form ) {
	form.querySelectorAll( '[aria-invalid="true"]' ).forEach( ( input ) => {
		input.removeAttribute( 'aria-invalid' );
	} );
	form.querySelectorAll( '.form-blocks-field__error' ).forEach( ( el ) => {
		el.textContent = '';
	} );
}

/**
 * Toggle disabled state and aria-busy on the form during submission, and
 * swap the submit button's label to reflect what's happening.
 *
 * Going busy sets plain text (the configured "Sending..." message is
 * admin-typed plain text, not markup). Coming back sets innerHTML instead,
 * to restore whatever RichText formatting the button originally had.
 *
 * @param {HTMLFormElement}         form
 * @param {HTMLButtonElement|null}  submitBtn
 * @param {boolean}                 busy
 * @param {string|null}             [label] Text (busy) or HTML (not busy) to show on the button.
 */
function setSubmitting( form, submitBtn, busy, label = null ) {
	form.setAttribute( 'aria-busy', busy ? 'true' : 'false' );
	if ( ! submitBtn ) return;

	submitBtn.disabled = busy;

	if ( busy ) {
		submitBtn.textContent = label;
	} else if ( null !== label ) {
		submitBtn.innerHTML = label;
	}
}

/**
 * Show the status element with a success or error class, and scroll it into
 * view — after an async submission the result can land outside the current
 * viewport (long forms, or a page scrolled while waiting), so the user
 * shouldn't have to go looking for it. Only scrolls if it isn't already
 * visible (block: "nearest"), so a message already on-screen doesn't jump.
 *
 * @param {Element|null} el
 * @param {boolean}      success
 * @param {string}       message
 */
function showStatus( el, success, message ) {
	if ( ! el ) return;
	el.textContent = message;
	el.className   = 'form-blocks-status form-blocks-status--' + ( success ? 'success' : 'error' );
	el.removeAttribute( 'hidden' );
	el.scrollIntoView( { behavior: 'smooth', block: 'nearest' } );
}

/**
 * Hide and clear the status element.
 *
 * @param {Element|null} el
 */
function hideStatus( el ) {
	if ( ! el ) return;
	el.textContent = '';
	el.setAttribute( 'hidden', '' );
}
